import React, { useState } from 'react'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import { Text } from 'rebass'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import Link, { InnerLink } from 'components/Infos/Link'
import Panel from 'components/Infos/Panel'
import TokenLogo from 'components/TokenImage'
import PairList from 'components/Infos/PairList'
import Loader from 'components/Loading'
import { AutoRow, RowBetween, RowFixed } from 'components/Row'
import Column, { AutoColumn } from 'components/Column'
import { ButtonLight, ButtonDark } from 'components/Infos/ButtonStyled'
import TxnList from 'components/Infos/TxnList'
import TokenChart from 'components/Infos/TokenChart'
import { BasicLink } from 'components/Infos/Link'
import { formattedNum, formattedPercent, getPoolLink, getSwapLink, localNumber } from 'utils/infos'
import { useTokenData, useTokenTransactions, useTokenPairs } from 'hooks/infos/TokenData'
import { TYPE } from 'theme'
import { EXPLORE_URL } from 'constants/index'
import CopyHelper from 'components/Infos/Copy'
import { useMedia } from 'react-use'
import { useDataForList } from 'hooks/infos/PairData'
import { useEffect } from 'react'
import Warning from 'components/Infos/Warning'
import { usePathDismissed, useSavedTokens } from 'hooks/infos/LocalStorage'
import { Hover, PageWrapper, ContentWrapper, StyledIcon } from 'components/Infos/index'
import { PlusCircle, Bookmark } from 'react-feather'
import FormattedName from 'components/Infos/FormattedName'
import { useExistedTokens } from 'hooks/infos/Application'

const DashboardWrapper = styled.div`
  width: 100%;
`

const PanelWrapper = styled.div`
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: max-content;
  gap: 6px;
  display: inline-grid;
  width: 100%;
  align-items: start;
  @media screen and (max-width: 1024px) {
    grid-template-columns: 1fr;
    align-items: stretch;
    > * {
      grid-column: 1 / 4;
    }

    > * {
      &:first-child {
        width: 100%;
      }
    }
  }
`

const TokenDetailsLayout = styled.div`
  display: inline-grid;
  width: 100%;
  grid-template-columns: auto auto auto 1fr;
  column-gap: 30px;
  align-items: start;

  &:last-child {
    align-items: center;
    justify-items: end;
  }
  @media screen and (max-width: 1024px) {
    grid-template-columns: 1fr;
    align-items: stretch;
    > * {
      grid-column: 1 / 4;
      margin-bottom: 1rem;
    }

    &:last-child {
      align-items: start;
      justify-items: start;
    }
  }
`

const WarningGrouping = styled.div<{ disabled?: boolean }>`
  opacity: ${({ disabled }) => disabled && '0.4'};
  pointer-events: ${({ disabled }) => disabled && 'none'};
`

function TokenPage({
  match,
  history
}: RouteComponentProps<{
  address: string
}>) {
  const { t } = useTranslation()
  const address = match.params.address.toLowerCase()
  const {
    // id,
    name,
    symbol,
    priceUSD = 0,
    oneDayVolumeUSD = 0,
    totalLiquidityUSD = 0,
    volumeChangeUSD = 0,
    oneDayVolumeUT = 0,
    volumeChangeUT = 0,
    priceChangeUSD = 0,
    liquidityChangeUSD = 0,
    oneDayTxns = 0,
    txnChange = 0
  } = useTokenData(address)

  useEffect(() => {
    document.querySelector('body')?.scrollTo(0, 0)
  }, [])

  // detect color from token

  const allPairs = useTokenPairs(address)

  // pairs to show in pair list
  const fetchedPairsList = useDataForList(allPairs)

  // console.log('pairs list', fetchedPairsList)

  // all transactions with this token
  const transactions = useTokenTransactions(address)

  // price
  const price = priceUSD ? formattedNum(priceUSD, true) : ''
  const priceChange = priceChangeUSD ? formattedPercent(priceChangeUSD) : ''

  // volume
  const volume =
    oneDayVolumeUSD || oneDayVolumeUSD === 0
      ? formattedNum(oneDayVolumeUSD === 0 ? (oneDayVolumeUT as number) : oneDayVolumeUSD, true)
      : oneDayVolumeUSD === 0
      ? '$0'
      : '-'

  // mark if using untracked volume
  const [usingUtVolume, setUsingUtVolume] = useState(false)
  useEffect(() => {
    setUsingUtVolume(oneDayVolumeUSD === 0 ? true : false)
  }, [oneDayVolumeUSD])

  const volumeChange = formattedPercent(!usingUtVolume ? volumeChangeUSD : volumeChangeUT)

  // liquidity
  const liquidity = totalLiquidityUSD ? formattedNum(totalLiquidityUSD, true) : totalLiquidityUSD === 0 ? '$0' : '-'
  const liquidityChange = formattedPercent(liquidityChangeUSD)

  // transactions
  const txnChangeFormatted = formattedPercent(txnChange)

  const below1080 = useMedia('(max-width: 1080px)')
  const below800 = useMedia('(max-width: 800px)')
  const below500 = useMedia('(max-width: 500px)')

  // format for long symbol
  const LENGTH = below1080 ? 10 : 16
  const formattedSymbol = symbol?.length > LENGTH ? symbol.slice(0, LENGTH) + '...' : symbol

  const [dismissed, markAsDismissed] = usePathDismissed(history.location.pathname)
  const [savedTokens, addToken] = useSavedTokens()
  const listedTokens = useExistedTokens()

  useEffect(() => {
    window.scrollTo({
      behavior: 'smooth',
      top: 0
    })
  }, [])

  return (
    <PageWrapper>
      <Warning
        type={'token'}
        show={!!(!dismissed && listedTokens && !listedTokens[address])}
        setShow={markAsDismissed}
        address={address}
      />
      <ContentWrapper>
        <RowBetween style={{ flexWrap: 'wrap', alignItems: 'start' }}>
          <AutoRow align="flex-end" style={{ width: 'fit-content' }}>
            <TYPE.title fontSize={26}>
              <BasicLink to="/analytics/tokens">
                <i className="iconfont icon-page-prev" style={{ fontSize: 26, marginRight: 24 }} />
                {t('analytics.tokens')}
              </BasicLink>
            </TYPE.title>
            {/* <Link style={{ width: 'fit-content' }} external href={`${EXPLORE_URL}/address/${address}`}>
              <Text style={{ marginLeft: '.15rem' }} fontSize={'14px'} fontWeight={400}>
                ({address.slice(0, 8) + '...' + address.slice(36, 42)})
              </Text>
            </Link> */}
          </AutoRow>
        </RowBetween>

        <WarningGrouping disabled={!!(!dismissed && listedTokens && !listedTokens[address])}>
          <DashboardWrapper style={{ marginTop: below1080 ? '0' : '1rem' }}>
            <RowBetween
              style={{
                flexWrap: 'wrap',
                marginBottom: '2rem',
                alignItems: 'flex-start'
              }}
            >
              <RowFixed style={{ flexWrap: 'wrap' }}>
                <RowFixed style={{ alignItems: 'baseline' }}>
                  <TokenLogo address={address} size="32px" style={{ alignSelf: 'center' }} />
                  <TYPE.v2Main fontSize={below1080 ? '1.5rem' : '2rem'} fontWeight={500} style={{ margin: '0 1rem' }}>
                    <RowFixed gap="6px">
                      <FormattedName text={name ? name + ' ' : ''} maxCharacters={16} style={{ marginRight: '6px' }} />{' '}
                      {formattedSymbol ? `(${formattedSymbol})` : ''}
                    </RowFixed>
                  </TYPE.v2Main>{' '}
                  {!below1080 && (
                    <>
                      <TYPE.mainLg fontSize={'1.5rem'} fontWeight={500} style={{ marginRight: '1rem' }}>
                        {price}
                      </TYPE.mainLg>
                      {priceChange}
                    </>
                  )}
                </RowFixed>
              </RowFixed>
              <span>
                <RowFixed ml={below500 ? '0' : '2.5rem'} mt={below500 ? '1rem' : '0'}>
                  {!!!savedTokens[address] && !below800 ? (
                    <Hover onClick={() => addToken(address, symbol)}>
                      <StyledIcon>
                        <PlusCircle style={{ marginRight: '0.5rem' }} />
                      </StyledIcon>
                    </Hover>
                  ) : !below1080 ? (
                    <StyledIcon>
                      <Bookmark style={{ marginRight: '0.5rem', opacity: 0.4 }} />
                    </StyledIcon>
                  ) : (
                    <></>
                  )}
                  <InnerLink to={getPoolLink(address)}>
                    <ButtonLight>+ {t('analytics.add_liquidity')}</ButtonLight>
                  </InnerLink>
                  <InnerLink to={getSwapLink(address)}>
                    <ButtonDark ml={'.5rem'} mr={below1080 ? '.5rem' : ''}>
                      {t('analytics.trade')}
                    </ButtonDark>
                  </InnerLink>
                </RowFixed>
              </span>
            </RowBetween>

            <>
              <PanelWrapper style={{ marginTop: below1080 ? '0' : '1rem' }}>
                {below1080 && price && (
                  <Panel>
                    <AutoColumn gap="20px">
                      <RowBetween>
                        <TYPE.v2Main>Price</TYPE.v2Main>
                        <div />
                      </RowBetween>
                      <RowBetween align="flex-end">
                        {' '}
                        <TYPE.mainLg fontSize={'1.5rem'} lineHeight={1} fontWeight={500}>
                          {price}
                        </TYPE.mainLg>
                        <TYPE.mainLg>{priceChange}</TYPE.mainLg>
                      </RowBetween>
                    </AutoColumn>
                  </Panel>
                )}
                <Panel>
                  <AutoColumn gap="20px">
                    <RowBetween>
                      <TYPE.v2Main>{t('analytics.total_liquidity')}</TYPE.v2Main>
                      <div />
                    </RowBetween>
                    <RowBetween align="flex-end">
                      <TYPE.mainLg fontSize={'1.5rem'} lineHeight={1} fontWeight={500}>
                        {liquidity}
                      </TYPE.mainLg>
                      <TYPE.mainLg>{liquidityChange}</TYPE.mainLg>
                    </RowBetween>
                  </AutoColumn>
                </Panel>
                <Panel>
                  <AutoColumn gap="20px">
                    <RowBetween>
                      <TYPE.v2Main>
                        {t('analytics.volume_24hrs')} {usingUtVolume && `(${t('analytics.untracked')})`}
                      </TYPE.v2Main>
                      <div />
                    </RowBetween>
                    <RowBetween align="flex-end">
                      <TYPE.mainLg fontSize={'1.5rem'} lineHeight={1} fontWeight={500}>
                        {volume}
                      </TYPE.mainLg>
                      <TYPE.mainLg>{volumeChange}</TYPE.mainLg>
                    </RowBetween>
                  </AutoColumn>
                </Panel>

                <Panel>
                  <AutoColumn gap="20px">
                    <RowBetween>
                      <TYPE.v2Main>{t('analytics.transactions_24hrs')}</TYPE.v2Main>
                      <div />
                    </RowBetween>
                    <RowBetween align="flex-end">
                      <TYPE.mainLg fontSize={'1.5rem'} lineHeight={1} fontWeight={500}>
                        {oneDayTxns ? localNumber(oneDayTxns) : oneDayTxns === 0 ? 0 : '-'}
                      </TYPE.mainLg>
                      <TYPE.mainLg>{txnChangeFormatted}</TYPE.mainLg>
                    </RowBetween>
                  </AutoColumn>
                </Panel>
                <Panel
                  style={{
                    gridColumn: below1080 ? '1' : '2/4',
                    gridRow: below1080 ? '' : '1/4'
                  }}
                >
                  <TokenChart address={address} base={priceUSD} />
                </Panel>
              </PanelWrapper>
            </>

            <span>
              <TYPE.v2Main fontSize={'1.125rem'} style={{ marginTop: '3rem' }}>
                {t('analytics.top_pairs')}
              </TYPE.v2Main>
            </span>
            <Panel
              rounded
              style={{
                marginTop: '1.5rem',
                padding: '30px 24px'
              }}
            >
              {address && Object.keys(fetchedPairsList).length > 0 ? (
                <PairList pairs={fetchedPairsList} />
              ) : (
                <Loader show={true} />
              )}
            </Panel>
            <RowBetween mt={40} mb={'1rem'}>
              <TYPE.v2Main fontSize={'1.125rem'}>{t('analytics.transactions')}</TYPE.v2Main> <div />
            </RowBetween>
            <Panel rounded>{transactions ? <TxnList transactions={transactions} /> : <Loader show={true} />}</Panel>
            <>
              <RowBetween style={{ marginTop: '3rem' }}>
                <TYPE.v2Main fontSize={'1.125rem'}>{t('analytics.token_info')}</TYPE.v2Main>{' '}
              </RowBetween>
              <Panel
                rounded
                style={{
                  marginTop: '1.5rem'
                }}
                p={20}
              >
                <TokenDetailsLayout>
                  <Column>
                    <TYPE.v2Main>{t('analytics.symbol')}</TYPE.v2Main>
                    <Text style={{ marginTop: '.5rem' }} fontSize={24} fontWeight="500">
                      <FormattedName text={symbol} maxCharacters={12} />
                    </Text>
                  </Column>
                  <Column>
                    <TYPE.v2Main>{t('analytics.name')}</TYPE.v2Main>
                    <TYPE.v2Main style={{ marginTop: '.5rem' }} fontSize={24} fontWeight="500">
                      <FormattedName text={name} maxCharacters={16} />
                    </TYPE.v2Main>
                  </Column>
                  <Column>
                    <TYPE.v2Main>Address</TYPE.v2Main>
                    <AutoRow align="flex-end">
                      <TYPE.v2Main style={{ marginTop: '.5rem' }} fontSize={24} fontWeight="500">
                        {address.slice(0, 8) + '...' + address.slice(36, 42)}
                      </TYPE.v2Main>
                      <CopyHelper toCopy={address} />
                    </AutoRow>
                  </Column>
                  <ButtonLight>
                    <Link external href={`${EXPLORE_URL}/address/${address}`}>
                      {t('analytics.view_on_metis_scan')} ↗
                    </Link>
                  </ButtonLight>
                </TokenDetailsLayout>
              </Panel>
            </>
          </DashboardWrapper>
        </WarningGrouping>
      </ContentWrapper>
    </PageWrapper>
  )
}

export default withRouter(TokenPage)
